import { type CustomAdventure } from '@/utils/businessAppApi';
import { type CustomAdventureBookingRequest } from '@/utils/customAdventureApi';

// Business data interface
export interface BusinessData {
  _id: string;
  name: string;
  description?: string;
  logo?: string;
  coverImage?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  contactInfo?: {
    email?: string;
    phone?: string;
  };
  rating?: number;
  reviewCount?: number;
  emoji?: string;
}

// Date range interface
export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

// Customer details interface
export interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  specialRequests?: string;
  preferredContactMethod: 'email' | 'phone' | 'whatsapp';
}

// Price breakdown item for customer-facing display
export interface PriceBreakdownItem {
  id?: string;
  label: string;
  description?: string;
  amount: number;
  type?: 'base' | 'option';
}

// Booking request interface
export interface BookingRequest {
  selectedOptions: Record<string, unknown>;
  totalPrice: number;
  dateRange: DateRange;
  customerDetails: CustomerDetails;
  flexibility: 'exact' | 'flexible_1_week' | 'flexible_2_weeks' | 'flexible_1_month';
  /** Full price breakdown for confirmation page and email */
  priceBreakdown?: PriceBreakdownItem[];
}

/**
 * Create API request from booking request
 */
export const createApiBookingRequest = (
  bookingRequest: BookingRequest,
  selectedAdventure: CustomAdventure
): CustomAdventureBookingRequest => {
  // Calculate participants count from form values
  const participants = {
    adults: Number(bookingRequest.selectedOptions['Number of Adults'] || 
            bookingRequest.selectedOptions['Number of People'] || 
            bookingRequest.selectedOptions['Adults'] || 1),
    children: Number(bookingRequest.selectedOptions['Number of Children'] || 
              bookingRequest.selectedOptions['Children'] || 0),
    students: Number(bookingRequest.selectedOptions['Number of Students'] || 
              bookingRequest.selectedOptions['Students'] || 0)
  };

  return {
    customAdventureId: selectedAdventure.id,
    businessId: selectedAdventure.businessId,
    requestedDateRange: {
      startDate: bookingRequest.dateRange.from!,
      endDate: bookingRequest.dateRange.to!,
      flexibility: bookingRequest.flexibility
    },
    selectedOptions: bookingRequest.selectedOptions,
    estimatedPrice: {
      basePrice: selectedAdventure.basePrice,
      optionsPrice: bookingRequest.totalPrice - selectedAdventure.basePrice,
      totalEstimate: bookingRequest.totalPrice,
      currency: selectedAdventure.currency
    },
    priceBreakdown: bookingRequest.priceBreakdown?.map(({ label, amount, description }) => ({
      label,
      amount,
      ...(description && { description })
    })),
    participants,
    customerDetails: bookingRequest.customerDetails
  };
};

/**
 * Transform business data from navigation state
 */
export const transformBusinessDataFromState = (
  stateData: {
    businessName: string;
    businessImage: string | null;
    businessDescription: string;
    businessLocation: any;
    businessEmoji: string;
  },
  businessId: string
): BusinessData => {
  return {
    _id: businessId,
    name: stateData.businessName,
    logo: stateData.businessImage || undefined,
    description: stateData.businessDescription,
    emoji: stateData.businessEmoji,
    address: stateData.businessLocation?.address
  };
};

/**
 * Transform business data from API response
 */
export const transformBusinessDataFromApi = (response: any): BusinessData => {
  return {
    _id: response._id,
    name: response.name,
    logo: response.images?.logo,
    description: response.description,
    emoji: response.emoji,
    address: response.location?.address,
    contactInfo: {
      email: response.contact?.email,
      phone: response.contact?.phone
    }
  };
};

/**
 * Session storage utilities
 */
export const sessionStorageUtils = {
  saveSelectedAdventure: (adventure: CustomAdventure, businessId: string) => {
    sessionStorage.setItem('selectedCustomAdventure', JSON.stringify({
      id: adventure.id,
      businessId: businessId
    }));
  },

  loadSelectedAdventure: (businessId: string) => {
    try {
      const savedAdventure = sessionStorage.getItem('selectedCustomAdventure');
      if (savedAdventure) {
        const parsedAdventure = JSON.parse(savedAdventure);
        // Only restore if the adventure belongs to the same business
        if (parsedAdventure.businessId === businessId) {
          return parsedAdventure;
        } else {
          // Clear saved data if business ID doesn't match
          sessionStorage.removeItem('selectedCustomAdventure');
        }
      }
    } catch (e) {
      console.error('Failed to parse saved adventure:', e);
      sessionStorage.removeItem('selectedCustomAdventure');
    }
    return null;
  },

  clearSelectedAdventure: () => {
    sessionStorage.removeItem('selectedCustomAdventure');
  },

  saveBookingRequest: (requestNumber: string) => {
    sessionStorage.setItem('lastBookingRequest', requestNumber);
  }
};
